import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { LocaleService } from 'src/app/services/locale';
import { LogoComponent } from '../../icons/logo';

// Interface for invite data (company from API)
interface InviteData {
  inviterCompanyId: string;
  inviterCompanyName: string;
}

@Component({
  selector: 'app-invite',
  imports: [CommonModule, RouterModule, LogoComponent],
  template: `
    <div class="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
      <div class="w-full max-w-md">
        <!-- Logo -->
        <div class="flex justify-center mb-8">
          <app-logo></app-logo>
        </div>

        <!-- Loading State -->
        <div *ngIf="isLoading" class="bg-white rounded-2xl shadow-xl p-8 text-center">
          <div class="flex flex-col items-center gap-4">
            <svg class="animate-spin h-10 w-10 text-[#043B22]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <p class="text-[#616161] futura">{{ locale.common.loading }}</p>
          </div>
        </div>

        <!-- Invalid/Expired Invite -->
        <div *ngIf="!isLoading && !invite" class="bg-white rounded-2xl shadow-xl overflow-hidden">
          <div class="p-8 text-center">
            <div class="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg class="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </div>
            <h2 class="text-xl futura text-[#043B22] mb-2">
              {{ locale.emission.invite_invalid }}
            </h2>
            <p class="text-sm text-[#616161] mb-6">
              {{ getInvalidLinkMessage() }}
            </p>
            <a
              routerLink="/login"
              class="inline-flex items-center gap-2 px-6 py-3 bg-[#043B22] text-white futura text-sm rounded-xl hover:bg-[#065a35] transition-all"
            >
              {{ language === 'english' ? 'Go to Login' : 'Vai al Login' }}
            </a>
          </div>
        </div>

        <!-- Valid Invite -->
        <div *ngIf="!isLoading && invite" class="bg-white rounded-2xl shadow-xl overflow-hidden">
          <!-- Header -->
          <div class="bg-gradient-to-r from-[#043B22] to-[#065a35] p-6 text-center">
            <div class="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center mx-auto mb-4">
              <svg class="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"/>
              </svg>
            </div>
            <h2 class="text-xl futura text-white mb-1">{{ locale.emission.invite_title }}</h2>
            <p class="text-white/80 text-sm">
              <span class="font-semibold">{{ invite.inviterCompanyName }}</span> {{ locale.emission.invite_desc }}
            </p>
          </div>

          <!-- Actions -->
          <div class="p-6">
            <p class="text-sm text-[#616161] text-center mb-6">
              {{ getAcceptMessage() }}
            </p>
            <div class="space-y-3">
              <a
                routerLink="/login"
                class="block w-full px-6 py-3 bg-[#043B22] text-white futura text-sm text-center rounded-xl hover:bg-[#065a35] transition-all"
              >
                {{ locale.emission.accept_invite }}
              </a>
              <a
                routerLink="/register"
                class="block w-full px-6 py-3 border-2 border-[#043B22] text-[#043B22] futura text-sm text-center rounded-xl hover:bg-[#043B22] hover:text-white transition-all"
              >
                {{ locale.emission.create_account_first }}
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  `
})
export class InviteComponent implements OnInit {
  private route = inject(ActivatedRoute);

  localeService = inject(LocaleService);
  locale = this.localeService.locale();
  language = this.localeService.getLanguage();

  token: string = '';
  invite: InviteData | null = null;
  isLoading = true;

  ngOnInit(): void {
    this.route.paramMap.subscribe(params => {
      this.token = params.get('token') || '';
      this.route.queryParamMap.subscribe(queryParams => {
        const companyName = queryParams.get('company') || '';
        this.invite = this.token && companyName
          ? { inviterCompanyId: this.token, inviterCompanyName: companyName }
          : null;
        this.isLoading = false;
      });
    });
  }

  getAcceptMessage(): string {
    return this.language === 'english'
      ? 'Login or create an account to accept this invitation.'
      : 'Accedi o crea un account per accettare questo invito.';
  }

  getInvalidLinkMessage(): string {
    return this.language === 'english'
      ? 'This invite link is no longer valid. Please ask the inviter to generate a new link.'
      : 'Questo link di invito non è più valido. Chiedi all\'invitante di generare un nuovo link.';
  }
}
